<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { useMapGetter } from 'dashboard/composables/store';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import CustomSlaConversationLink from './CustomSlaConversationLink.vue';

const props = defineProps({
  pageIndex: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(['pageChange']);
const { t } = useI18n();

const conversations = useMapGetter('customSlaReports/getConversations');
const uiFlags = useMapGetter('customSlaReports/getUIFlags');

const isLoading = computed(() => uiFlags.value.isFetchingConversations);

const tableData = computed(() =>
  conversations.value.records.map(row => ({
    id: row.id,
    contactName: row.contact_name || '',
    assigneeName: row.assignee_name || '',
    breachCount: row.breach_count || 0,
  }))
);

const columnHelper = createColumnHelper();
const defaultSpanRender = cellProps => h('span', {}, cellProps.getValue());

const columns = computed(() => [
  columnHelper.accessor('id', {
    header: t('CUSTOM_SLA_REPORTS.CONVERSATIONS_TABLE.HEADER.TICKET'),
    cell: cellProps => h(CustomSlaConversationLink, cellProps),
  }),
  columnHelper.accessor('contactName', {
    header: t('CUSTOM_SLA_REPORTS.CONVERSATIONS_TABLE.HEADER.CONTACT'),
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('assigneeName', {
    header: t('CUSTOM_SLA_REPORTS.CONVERSATIONS_TABLE.HEADER.AGENT'),
    cell: defaultSpanRender,
  }),
  columnHelper.accessor('breachCount', {
    header: t('CUSTOM_SLA_REPORTS.CONVERSATIONS_TABLE.HEADER.BREACH_COUNT'),
    cell: defaultSpanRender,
  }),
]);

const paginationParams = computed(() => ({
  pageIndex: props.pageIndex,
  pageSize: 25,
}));

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  get columns() {
    return columns.value;
  },
  manualPagination: true,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  get rowCount() {
    return conversations.value.count;
  },
  state: {
    get pagination() {
      return paginationParams.value;
    },
  },
  onPaginationChange: updater => {
    const newPagination = updater(paginationParams.value);
    emit('pageChange', newPagination.pageIndex);
  },
});
</script>

<template>
  <div
    class="relative flex flex-col w-full gap-3 overflow-auto px-4 py-4 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <span class="text-heading-3 text-n-slate-12">
      {{ $t('CUSTOM_SLA_REPORTS.CONVERSATIONS_TABLE.TITLE') }}
    </span>
    <woot-loading-state
      v-if="isLoading"
      :message="$t('CUSTOM_SLA_REPORTS.LOADING')"
    />
    <template v-else>
      <Table :table="table" />
      <EmptyState
        v-if="!tableData.length"
        :title="$t('CUSTOM_SLA_REPORTS.CONVERSATIONS_TABLE.NO_DATA')"
      />
      <Pagination v-if="conversations.count" :table="table" />
    </template>
  </div>
</template>
